/** Shared navigation, tenant identity and profile controls. */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Command, Sun, Moon, ChevronDown, Check, Rows3, Rows2, Menu,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveRoleKey } from '../../pages/dashboard/roleLayouts';
import { SIDEBAR_VIEWS, SIDEBAR_VIEW_GROUPS, type SidebarView } from './Sidebar';
import { resolveProfile, isPlatformAdmin } from '../../lib/profileIdentity';
import { TopBarNotifications } from './TopBarNotifications';
import { TopBarAccount } from './TopBarAccount';
import { TopBarCredits } from './TopBarCredits';
import { IconButton, Dropdown, DropdownLabel, DropdownItem, DropdownDivider } from './TopBarPrimitives';
import { CallAvailabilityChip } from './CallAvailabilityChip';

interface TopBarProps {
  onCmdOpen: () => void;
  onAiOpen: () => void;
  density: 'compact' | 'comfortable';
  onDensityChange: (d: 'compact' | 'comfortable') => void;
  viewOverride: SidebarView | null;
  onViewOverrideChange: (v: SidebarView | null) => void;
  /** True when viewport is <=768px — shows hamburger, hides desktop controls */
  isMobile?: boolean;
  /** Hamburger tap handler — opens mobile sidebar drawer */
  onHamburger?: () => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

function tenantInitial(name?: string) {
  if (!name) return 'A';
  return name.trim()[0]?.toUpperCase() ?? 'A';
}

/* Deterministic warm-palette accent so two tenants in a row don't collide. */
function tenantAccent(slug: string | undefined | null): string {
  if (!slug) return 'var(--saffron-600)';
  const palette = ['#C98A3B', '#2F7A5B', '#3A6A8C', '#8B4A6B', '#6B5B8C', '#B87A2E'];
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export function TopBar({
  onCmdOpen,
  onAiOpen,
  density,
  onDensityChange,
  viewOverride,
  onViewOverrideChange,
  isMobile = false,
  onHamburger,
}: TopBarProps) {
  const navigate = useNavigate();

  const { user, tenant } = useAuthStore();
  const { resolvedTheme, toggleTheme } = useTheme();

  const [tenantOpen, setTenantOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);


  // Refs for outside-click detection
  const rootRef = useRef<HTMLDivElement | null>(null);

  const roleName = (user?.role?.name ?? '').toLowerCase();
  const isAdmin = resolveProfile(user) === 'P18';
  // May 26, 2026 — strict-view roles hide the global search (no perms to
  // read contacts/deals/invoices anyway; UI mirrors backend gating).
  const isStrictView = roleName === 'lead_triage_coordinator';
  const isSuperAdmin = isPlatformAdmin(user);

  // Outside-click closes any open popover
  useEffect(() => {
    if (!tenantOpen && !roleOpen && !deptOpen && !notifOpen && !userMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setTenantOpen(false);
        setRoleOpen(false);
        setDeptOpen(false);
        setNotifOpen(false);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [tenantOpen, roleOpen, deptOpen, notifOpen, userMenuOpen]);

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';
  const displayName = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : 'User';
  const roleLabel = user?.role?.displayName ?? user?.role?.name ?? '—';
  const roleKey = resolveRoleKey(user as never);
  const accent = tenantAccent(tenant?.slug);

  return (
    <div
      ref={rootRef}
      className="relative flex items-center gap-2 px-4"
      style={{
        height: 52,
        background: 'var(--ivory-1)',
        borderBottom: '1px solid var(--line)',
        color: 'var(--ink-1)',
        zIndex: 30,
        flexShrink: 0,
      }}
    >
      {/* Hamburger — mobile only (md:hidden via isMobile prop check) */}
      {isMobile && (
        <button
          type="button"
          onClick={onHamburger}
          aria-label="Open navigation"
          className="flex items-center justify-center rounded-md"
          style={{ width: 34, height: 34, color: 'var(--ink-2)', flexShrink: 0 }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Menu size={18} />
        </button>
      )}

      {/* Logo */}
      <div className="flex w-auto items-center gap-2.5 xl:w-[180px]" style={{ flexShrink: 0 }}>
        <div
          className="flex items-center justify-center"
          style={{
            width: 28, height: 28, borderRadius: 7,
            background: 'linear-gradient(135deg, #1B1713, #3A332B)',
            color: '#FEFCF7',
            fontFamily: 'var(--ff-serif)', fontWeight: 600, fontSize: 15,
            letterSpacing: -0.5,
          }}
        >A</div>
        <div style={{ fontFamily: 'var(--ff-serif)', fontWeight: 600, fontSize: 17, color: 'var(--ink-0)' }}>
          Aura
        </div>
      </div>

      {/* Tenant switcher — desktop only */}
      {!isMobile && (
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setTenantOpen((o) => !o);
              setRoleOpen(false); setDeptOpen(false); setNotifOpen(false); setUserMenuOpen(false);
            }}
            className="flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[13px]"
            style={{
              minWidth: 200, maxWidth: 240,
              background: 'var(--ivory-0)',
              borderColor: 'var(--line)',
              color: 'var(--ink-0)',
            }}
          >
            <div
              className="flex items-center justify-center rounded font-semibold"
              style={{
                width: 18, height: 18, fontSize: 11,
                background: accent, color: '#fff',
                flexShrink: 0,
              }}
            >
              {tenantInitial(tenant?.name)}
            </div>
            <span className="flex-1 truncate text-left" style={{ fontWeight: 500 }}>
              {tenant?.name ?? 'No organisation'}
            </span>
            <ChevronDown size={13} style={{ opacity: 0.5 }} />
          </button>
          {tenantOpen && (
            <Dropdown width={280}>
              <DropdownLabel>Current organisation</DropdownLabel>
              <DropdownItem>
                <div
                  className="flex items-center justify-center rounded font-semibold"
                  style={{ width: 22, height: 22, fontSize: 12, background: accent, color: '#fff', flexShrink: 0 }}
                >
                  {tenantInitial(tenant?.name)}
                </div>
                <div className="flex-1">
                  <div style={{ fontWeight: 500 }}>{tenant?.name ?? '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {tenant?.slug ?? ''}
                  </div>
                </div>
                <Check size={14} style={{ color: 'var(--forest-600)' }} />
              </DropdownItem>
              {isSuperAdmin && (
                <>
                  <DropdownDivider />
                  <DropdownItem onClick={() => { navigate('/admin/tenants'); setTenantOpen(false); }}>
                    <span style={{ color: 'var(--ink-3)' }}>Manage tenants…</span>
                  </DropdownItem>
                </>
              )}
            </Dropdown>
          )}
        </div>
      )}

      {/* Search / ⌘K — desktop only; hidden for strict-view roles (no read perms) */}
      {!isMobile && !isStrictView && (
        <button
          type="button"
          onClick={onCmdOpen}
          aria-label="Search contacts, leads, invoices"
          className="flex items-center gap-2 rounded-md border text-[13px]"
          style={{
            flex: 1, minWidth: 90, maxWidth: 420,
            height: 32, padding: '0 10px',
            background: 'var(--ivory-2)',
            borderColor: 'var(--line)',
            color: 'var(--ink-3)',
            cursor: 'pointer',
          }}
        >
          <Search size={14} className="shrink-0" />
          <span className="flex-1 text-left truncate"><span className="xl:hidden">Search</span><span className="hidden xl:inline">Search contacts, leads, invoices…</span></span>
          <span
            className="hidden shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-mono xl:inline"
            style={{
              background: 'var(--ivory-1)',
              borderColor: 'var(--line)',
              color: 'var(--ink-3)',
            }}
          >⌘K</span>
        </button>
      )}

      <div className="flex-1" />

      {/* Admin-only dept dropdown — desktop only */}
      {!isMobile && (isAdmin || isSuperAdmin) && (
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setDeptOpen((o) => !o);
              setTenantOpen(false); setRoleOpen(false); setNotifOpen(false); setUserMenuOpen(false);
            }}
            className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px]"
            style={{
              background: viewOverride ? 'var(--saffron-50)' : 'var(--ivory-0)',
              borderColor: viewOverride ? 'var(--saffron-100)' : 'var(--line)',
              color: viewOverride ? 'var(--saffron-600)' : 'var(--ink-2)',
            }}
            title="View sidebar as another role"
          >
            <span>{viewOverride ? `View: ${labelForView(viewOverride)}` : 'View as…'}</span>
            <ChevronDown size={11} style={{ opacity: 0.5 }} />
          </button>
          {deptOpen && (
            <Dropdown width={240} align="right">
              <DropdownLabel>Browse sidebar as</DropdownLabel>
              <DropdownItem
                onClick={() => { onViewOverrideChange(null); setDeptOpen(false); }}
              >
                <span className="flex-1">My role ({roleLabel})</span>
                {!viewOverride && <Check size={14} style={{ color: 'var(--forest-600)' }} />}
              </DropdownItem>
              {/* Grouped: Managers / Executives (Apr 22, 2026 exec sprint) */}
              {SIDEBAR_VIEW_GROUPS.map((group, gIdx) => (
                <div key={group.heading}>
                  {gIdx === 0 ? <DropdownDivider /> : <DropdownDivider />}
                  <DropdownLabel>{group.heading}</DropdownLabel>
                  {group.views.map((v) => (
                    <DropdownItem
                      key={v.id}
                      onClick={() => { onViewOverrideChange(v.id); setDeptOpen(false); }}
                    >
                      <span className="flex-1">{v.label}</span>
                      {viewOverride === v.id && <Check size={14} style={{ color: 'var(--forest-600)' }} />}
                    </DropdownItem>
                  ))}
                </div>
              ))}
            </Dropdown>
          )}
        </div>
      )}

      {/* Role chip — desktop only */}
      {!isMobile && (
        <div className="relative">
          <button
            type="button"
            onClick={() => {
              setRoleOpen((o) => !o);
              setTenantOpen(false); setDeptOpen(false); setNotifOpen(false); setUserMenuOpen(false);
            }}
            className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px]"
            style={{
              background: 'var(--ivory-0)',
              borderColor: 'var(--line)',
              color: 'var(--ink-2)',
            }}
            title="Role info"
          >
            <span>{roleLabel}</span>
            <ChevronDown size={11} style={{ opacity: 0.5 }} />
          </button>
          {roleOpen && (
            <Dropdown width={240} align="right">
              <DropdownLabel>Signed in as</DropdownLabel>
              <div className="px-2.5 py-2" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                <div style={{ fontWeight: 500, color: 'var(--ink-0)' }}>{displayName}</div>
                <div>{user?.email}</div>
                <div className="mt-1" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Role: {roleLabel}
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Key: {roleKey}
                </div>
                {user?.department?.name && (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    Department: {user.department.name}
                  </div>
                )}
              </div>
            </Dropdown>
          )}
        </div>
      )}

      {/* Call Availability chip — desktop only; role-gated inside the component */}
      {!isMobile && <CallAvailabilityChip />}

      {/* Density toggle — desktop only */}
      {!isMobile && (
        <IconButton
          onClick={() => onDensityChange(density === 'compact' ? 'comfortable' : 'compact')}
          title={density === 'compact' ? 'Switch to comfortable' : 'Switch to compact'}
        >
          {density === 'compact' ? <Rows3 size={14} /> : <Rows2 size={14} />}
        </IconButton>
      )}

      {/* Theme toggle */}
      <IconButton
        onClick={toggleTheme}
        title={resolvedTheme === 'dark' ? 'Light mode' : 'Dark mode'}
      >
        {resolvedTheme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
      </IconButton>

      {!isMobile && <TopBarCredits tenantSlug={tenant?.slug} />}

      {/* AI assistant (⌘J) — desktop only */}
      {!isMobile && (
        <button
          type="button"
          onClick={onAiOpen}
          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px]"
          style={{ color: 'var(--ink-2)', background: 'transparent' }}
          title="AI Assistant · ⌘J"
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Command size={13} />
          <span className="font-mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>⌘J</span>
        </button>
      )}

      <TopBarNotifications open={notifOpen} onClose={() => setNotifOpen(false)} onToggle={() => {
        setNotifOpen(o => !o); setTenantOpen(false); setRoleOpen(false); setDeptOpen(false); setUserMenuOpen(false);
      }} />

      <TopBarAccount open={userMenuOpen} onClose={() => setUserMenuOpen(false)}
        displayName={displayName} initials={initials} accent={accent} onToggle={() => {
          setUserMenuOpen(o => !o); setTenantOpen(false); setRoleOpen(false); setDeptOpen(false); setNotifOpen(false);
        }} />
    </div>
  );
}

function labelForView(v: SidebarView): string {
  const found = SIDEBAR_VIEWS.find((x) => x.id === v);
  return found?.label ?? v;
}
